import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Eye, Trash2, ArrowLeft, RefreshCw, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { adminApi } from "@/lib/adminApi";

const EMPTY_POST = {
  id: null,
  title: "",
  slug: "",
  excerpt: "",
  body_html: "",
  author: "Regulens Team",
  publish_date: toLocalInput(new Date().toISOString()),
  status: "draft",
};

function slugify(t) {
  return (t || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Convert an ISO datetime to a `datetime-local` compatible string (YYYY-MM-DDTHH:MM).
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminBlogTab() {
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = list view; else post object

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/blog/posts");
      setPosts(data.posts || []);
    } catch { setPosts([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const startNew = () => {
    setEditing({ ...EMPTY_POST, publish_date: toLocalInput(new Date().toISOString()) });
  };

  const startEdit = async (post) => {
    try {
      const { data } = await adminApi.get(`/admin/blog/posts/id/${post.id}`);
      setEditing({
        ...data.post,
        publish_date: toLocalInput(data.post.publish_date),
      });
    } catch { toast.error("Could not load post"); }
  };

  const previewPost = (post) => {
    // Open preview in a new tab so the editor is preserved.
    const url = `${window.location.origin}/blog/preview/${post.id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const previewCurrent = async () => {
    if (!editing) return;
    // If new post — save as draft first so a preview URL exists.
    if (!editing.id) {
      const saved = await save({ silent: true });
      if (saved?.id) previewPost(saved);
      return;
    }
    // Otherwise save the current state, then preview.
    const saved = await save({ silent: true });
    if (saved?.id) previewPost(saved);
  };

  const save = async ({ publish = null, silent = false } = {}) => {
    if (!editing) return null;
    if (!editing.title.trim()) {
      if (!silent) toast.error("Title is required");
      return null;
    }
    const payload = {
      title: editing.title,
      slug: editing.slug || slugify(editing.title),
      excerpt: editing.excerpt,
      body_html: editing.body_html,
      author: editing.author,
      publish_date: fromLocalInput(editing.publish_date),
      status: publish === "published" ? "published" : publish === "draft" ? "draft" : editing.status,
    };
    try {
      let res;
      if (editing.id) {
        res = await adminApi.put(`/admin/blog/posts/${editing.id}`, payload);
      } else {
        res = await adminApi.post(`/admin/blog/posts`, payload);
      }
      const post = res.data.post;
      setEditing({ ...post, publish_date: toLocalInput(post.publish_date) });
      if (!silent) toast.success(post.status === "published" ? "Published" : "Saved as draft");
      refresh();
      return post;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Save failed");
      return null;
    }
  };

  const unpublish = async (postId) => {
    if (!window.confirm("Unpublish this post? It will no longer appear on the public blog.")) return;
    try {
      await adminApi.post(`/admin/blog/posts/${postId}/unpublish`);
      toast.success("Unpublished");
      refresh();
    } catch { toast.error("Unpublish failed"); }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}" permanently? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/blog/posts/${post.id}`);
      toast.success("Deleted");
      refresh();
    } catch { toast.error("Delete failed"); }
  };

  // ---------- Editor view ----------
  if (editing) {
    return (
      <EditorView
        editing={editing}
        setEditing={setEditing}
        onCancel={() => setEditing(null)}
        onSaveDraft={() => save({ publish: "draft" })}
        onPublish={() => save({ publish: "published" })}
        onPreview={previewCurrent}
      />
    );
  }

  // ---------- List view ----------
  return (
    <div data-testid="admin-blog-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-heading font-bold text-[var(--rg-text-strong)]">Blog Manager</h2>
          <p className="text-xs text-[var(--rg-muted)]">Create, edit, and publish blog posts. Preview opens in a new tab.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={refresh} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="admin-blog-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button size="sm" onClick={startNew} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="admin-blog-new">
            <Plus className="w-4 h-4 mr-1.5" /> New Post
          </Button>
        </div>
      </div>

      {loading && <div className="text-[var(--rg-muted)] text-sm py-6"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>}

      {!loading && posts && posts.length === 0 && (
        <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-8 text-center text-[var(--rg-text)]" data-testid="admin-blog-empty">
          No posts yet. Click <span className="font-semibold text-[var(--rg-text-strong)]">New Post</span> to create your first article.
        </div>
      )}

      {!loading && posts && posts.length > 0 && (
        <div className="rounded-md border border-[var(--rg-border)] overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-blog-table">
            <thead className="bg-[var(--rg-bg)] text-[var(--rg-muted)] text-[10px] uppercase tracking-[0.14em]">
              <tr>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2 w-24">Status</th>
                <th className="text-left px-3 py-2 w-40">Publish Date</th>
                <th className="text-right px-3 py-2 w-20">Words</th>
                <th className="text-right px-3 py-2 w-20">Views</th>
                <th className="text-right px-3 py-2 w-52">Actions</th>
              </tr>
            </thead>
            <tbody className="text-[var(--rg-text)]">
              {posts.map((p) => (
                <tr key={p.id} className="border-t border-[var(--rg-border)] hover:bg-[var(--rg-surface)]" data-testid={`admin-blog-row-${p.slug}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-[var(--rg-text-strong)]">{p.title}</div>
                    <div className="text-[11px] text-[var(--rg-muted)] font-mono">/blog/{p.slug}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${
                      p.status === "published"
                        ? "text-[var(--rg-sage)] border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]"
                        : "text-[var(--rg-amber)] border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)]"
                    }`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[var(--rg-text)]">{formatDate(p.publish_date)}</td>
                  <td className="px-3 py-2 text-right">{p.word_count || 0}</td>
                  <td className="px-3 py-2 text-right font-semibold" data-testid={`admin-blog-views-${p.slug}`}>{p.views ?? 0}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => startEdit(p)} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] h-7 text-[11px]" data-testid={`admin-blog-edit-${p.slug}`}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => previewPost(p)} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] h-7 text-[11px]" data-testid={`admin-blog-preview-${p.slug}`}>
                        <Eye className="w-3 h-3 mr-1" />Preview
                      </Button>
                      {p.status === "published" && (
                        <Button size="sm" variant="outline" onClick={() => unpublish(p.id)} className="border-[var(--rg-amber)] text-[var(--rg-amber)] hover:bg-[var(--rg-amber-soft)] h-7 text-[11px]" data-testid={`admin-blog-unpublish-${p.slug}`}>
                          <EyeOff className="w-3 h-3 mr-1" />Unpublish
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => remove(p)} className="border-[var(--rg-red)] text-[var(--rg-red)] hover:bg-[var(--rg-red-soft)] h-7 text-[11px]" data-testid={`admin-blog-delete-${p.slug}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EditorView({ editing, setEditing, onCancel, onSaveDraft, onPublish, onPreview }) {
  const set = (patch) => setEditing((prev) => ({ ...prev, ...patch }));
  const slugPreview = editing.slug || slugify(editing.title);
  const excerptRemaining = 300 - (editing.excerpt || "").length;

  return (
    <div data-testid="admin-blog-editor">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onCancel}
          className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] text-sm inline-flex items-center gap-1"
          data-testid="admin-blog-editor-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to posts
        </button>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onPreview} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="admin-blog-editor-preview">
            <Eye className="w-4 h-4 mr-1.5" /> Preview
          </Button>
          <Button size="sm" variant="outline" onClick={onSaveDraft} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="admin-blog-editor-save-draft">
            <Save className="w-4 h-4 mr-1.5" /> Save as Draft
          </Button>
          <Button size="sm" onClick={onPublish} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="admin-blog-editor-publish">
            Publish Now
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        {/* Title */}
        <div>
          <input
            type="text"
            placeholder="Post title…"
            value={editing.title}
            onChange={(e) => set({ title: e.target.value, slug: editing.slug || slugify(e.target.value) })}
            className="w-full bg-transparent border-b border-[var(--rg-border)] focus:border-[var(--rg-sage)] outline-none text-[var(--rg-text-strong)] py-2"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 700 }}
            data-testid="admin-blog-editor-title"
          />
        </div>

        {/* Slug + URL preview */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--rg-muted)]">Slug</Label>
            <Input
              value={editing.slug}
              onChange={(e) => set({ slug: slugify(e.target.value) })}
              placeholder="auto-generated-from-title"
              className="bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1"
              data-testid="admin-blog-editor-slug"
            />
            <p className="text-[11px] text-[var(--rg-muted)] mt-1 font-mono" data-testid="admin-blog-editor-url-preview">
              getregulens.com/blog/<span className="text-[var(--rg-sage)]">{slugPreview || "your-slug"}</span>
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--rg-muted)]">Author</Label>
            <Input
              value={editing.author}
              onChange={(e) => set({ author: e.target.value })}
              className="bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1"
              data-testid="admin-blog-editor-author"
            />
          </div>
        </div>

        {/* Excerpt */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-[var(--rg-muted)]">Excerpt (used for blog index card + meta description)</Label>
          <Textarea
            value={editing.excerpt}
            onChange={(e) => set({ excerpt: e.target.value.slice(0, 300) })}
            rows={3}
            className="bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1"
            data-testid="admin-blog-editor-excerpt"
          />
          <p className="text-[11px] text-[var(--rg-muted)] mt-1 text-right font-mono" data-testid="admin-blog-editor-excerpt-counter">
            {editing.excerpt.length}/300
          </p>
        </div>

        {/* Publish date */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--rg-muted)]">Publish Date &amp; Time</Label>
            <Input
              type="datetime-local"
              value={editing.publish_date}
              onChange={(e) => set({ publish_date: e.target.value })}
              className="bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1"
              data-testid="admin-blog-editor-publish-date"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--rg-muted)]">Current status</Label>
            <div
              className={`mt-2 px-3 py-2 rounded-sm inline-block text-[11px] font-bold uppercase tracking-wider ${
                editing.status === "published"
                  ? "text-[var(--rg-sage)] border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]"
                  : "text-[var(--rg-amber)] border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)]"
              }`}
              data-testid="admin-blog-editor-status"
            >
              {editing.status}
            </div>
          </div>
        </div>

        {/* Body */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-[var(--rg-muted)]">
            Body (HTML — supports &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;/&lt;li&gt;, &lt;blockquote&gt;, &lt;a href&gt;)
          </Label>
          <Textarea
            value={editing.body_html}
            onChange={(e) => set({ body_html: e.target.value })}
            rows={15}
            placeholder={"<h2>Section heading</h2>\n<p>Your paragraph text. Use <strong>bold</strong> for emphasis, and <a href=\"https://example.com\">links</a> in-line.</p>\n<ul><li>Bullet one</li><li>Bullet two</li></ul>\n<blockquote>A pull-quote.</blockquote>"}
            className="mt-1 bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)] font-mono text-[13px] leading-relaxed"
            style={{ minHeight: "380px", resize: "vertical" }}
            data-testid="admin-blog-editor-body"
          />
          <p className="text-[11px] text-[var(--rg-muted)] mt-1">
            Drag the bottom-right corner to expand vertically. Plain HTML — no external editor required.
          </p>
        </div>
      </div>
    </div>
  );
}
